//Imports
import React, { Fragment, useEffect, useMemo, useRef, useState, useCallback } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
  Container,
  TableView,
  SearchInput,
  TableButtons,
  MenuComponent,
  useTablePagination
} from "@gpstrackit/gtc-react-controls";

//Components
import { Grid } from "@mui/material";
import { NotificationManager } from "Components/Notifications";
import Preferences from "Core/containers/Preferences";

//Actions
import { reset, manageSearchFuelCards } from "./redux/slice";
import { sendGenericExport, clearRedux, savePreference } from "Redux/actions";
import { openModalEditManageFuelCard } from "./modals/editManageFuelCard/redux/slice";
import EditManageFuelCard from "./modals/editManageFuelCard";

//Helpers
import { completeColumns } from "Core/data/Helpers";
import { HasPermission } from "Core/security"

const KEY_PREFERENCE = "Manage-FuelCardTa-list";
const INITIAL_SORT = { key: "cardNumber", order: "asc" };

const FuelCardTab = () => {
  //States
  const [preferences, setPreferences] = useState({});
  const [loadPreferences, setLoadPreferences] = useState(true);
  const [loadColumns, setLoadColumns] = useState(false);

  //Refs
  const fuelCardTableRef = useRef();

  //Language
  const { messages, locale } = useIntl();

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [344])

  //Redux
  const dispatch = useDispatch();
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const { loading, fuelCards, total, error } = useSelector((state) => state.manageFuelCardTabRedux);
  const openEditManageFuelCard = useSelector((state) => state.manageEditManageFuelCardRedux.open);
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const pagination = useTablePagination({
    dispatch,
    action: manageSearchFuelCards,
    searchFields: ["cardNumber", "unit_tankSize"],
    exportName: "fuelCards",
    sendGenericExport,
    tableRef: fuelCardTableRef
  });

  if (error) {
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
    "warning"
    );
    dispatch(reset({ error: null }));
  }

  if (exportResult && exportName === "fuelCards") {
    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }

  //Options
  const menuFuelCardTableOptions = [messages["edit"]];

  //Memos
  const currentColumns = useMemo(
    () => [
      {
        key: "cardNumber",
        title: messages["manage_fuelCard_cardNumberColumnTitle"],
        functionString: 'return row?.cardNumber || ""',
        sort: true,
        show: true,
        export: true,
        exportValue: (row) => row?.cardNumber,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row.cardNumber}</p>
            </Grid>
            {editablePermissions &&
              <Grid item>
                <MenuComponent
                  placement="right-start"
                  disabled={loading}
                  options={menuFuelCardTableOptions}
                  icon="more_vert"
                  className="showInHover"
                  onClick={handleClickMenu}
                  value={row}
                />
              </Grid>
            }
          </Grid>
        )
      },
      {
        key: "vehicleId",
        title: messages["manage_fuelCard_vehicleColumnTitle"],
        functionString: 'return row?.vehicleId || ""',
        show: true,
        export: true,
        exportValue: (row) => row?.vehicleId,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row?.vehicleId}</p>
            </Grid>
          </Grid>
        )
      },
      {
        key: "vehicleDescription",
        title: messages["manage_fuelCard_vehicleDescriptionColumnTitle"],
        functionString: 'return row?.vehicleDescription || ""',
        show: true,
        export: true,
        exportValue: (row) => row?.vehicleDescription,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row?.vehicleDescription}</p>
            </Grid>
          </Grid>
        )
      },
      {
        key: "unitOrDriver",
        title: messages["manage_fuelCard_unitOrDriverColumnTitle"],
        functionString: 'return row?.unit?.label || row?.driver?.name || ""',
        show: true,
        export: true,
        exportValue: (row) => row?.unit?.label || row?.driver?.name,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row?.unit?.label || row?.driver?.name}</p>
            </Grid>
          </Grid>
        )
      },
      {
        key: "tankSize",
        title: messages["manage_fuelCard_tankSizeColumnTitle"],
        functionString: 'return row?.unit?.tankSize ? row?.unit?.tankSize + " G" : ""',
        show: true,
        export: true,
        exportValue: (row) => row?.unit?.tankSize ? row?.unit?.tankSize + " G" : "",
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row?.unit?.tankSize ? row?.unit?.tankSize + " G" : ""}</p>
            </Grid>
          </Grid>
        )
      }
    ],
    [locale]
  );

  //Utils and handlers
  const handleClickMenu = (type, fuelCard) => {
    if (type === messages["edit"]) {
      dispatch(openModalEditManageFuelCard(fuelCard));
      return;
    }
  };

  const savePreferences = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
      }

      let updatedPreferences = { ...preferences } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferences(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: KEY_PREFERENCE,
          preferenceValue: updatedPreferences
        })
      );
    },
    [preferences]
  );

  //Effects
  useEffect(() => {
    if (preferences?.columns?.length && !loadColumns) {
      const preferenceMap = new Map(preferences.columns.map((col) => [col.key, col]));

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferences.columns
        .map((pref) => {
          const col = currentColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      pagination.setColumns(finalColumns);
      setPreferences({ ...preferences });
      setLoadColumns(true);
    } else {
      pagination.setColumns(currentColumns);
    }
  }, [preferences.columns, loadColumns]);
  

  return (
    <Fragment>
      <Container maxWidth={false}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                <TableButtons
                  messages={messages}
                  buttons={["export", "orderColumn"]}
                  {...pagination}
                  onChangeColumns={(columns) => {
                    savePreferences([{ key: "columns", value: columns }]);
                    pagination.setColumns(columns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={5} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_fuelCard_search_by_fuel_card_number"]}
              searchText={pagination.searchText}
              onSearchText={pagination.onSearchChange}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={KEY_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferences) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT; // * Initial sort
              fuelCardTableRef.current?.setSort?.(sort);
              pagination.onChangeSort(sort);
            }, 100);
            setPreferences(data);
            setLoadPreferences(false);
          }}
        >
          <TableView
            keyColumn="id"
            loading={loading || loadPreferences}
            topheight={235}
            data={fuelCards}
            total={total}
            exportOrientation="landscape"
            {...pagination}
            disableItemSelection
            onChangeColumns={(columns) => {
              const newcurrentColumns = pagination?.columns || [];
              const finalsColumns = completeColumns(newcurrentColumns, columns);
              savePreferences([{ key: "columns", value: finalsColumns }]);
              pagination.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              savePreferences([{ key: "sort", value: newSort }]);
              pagination.onChangeSort(newSort);
            }}
          />
        </Preferences>
      </Container>

      {openEditManageFuelCard && (
        <EditManageFuelCard refresh={fuelCardTableRef?.current?.refreshTable} />
      )}
    </Fragment>
  );
};

export default FuelCardTab;
